import { Widget, type Layer, type WidgetProps } from '@deck.gl/core';
import classes from './MapLoadIndicator.module.css';

const DEBOUNCE_MS = 500;

type LoadWidgetProps = WidgetProps & {
  delay?: number;
  devModeEnabled: boolean;
};

// This class encapsulates interaction with deck.gl layers through the Widget interface
// Note: I used this approach rather than a react component (via useWidget) because that ties
// the component to the deck.gl context, which updates very frequently and forces re-renders
// (which noticeably interrupt the loading wheel animation).
export class LoadWidget extends Widget<LoadWidgetProps> {
  // This widget is actually center-bottom,
  // but we use `bottom-right` to avoid
  // Typescript issues with deck.gl
  placement = 'bottom-right' as const;
  className = 'deck-widget-map-load-indicator';
  element: HTMLDivElement | null = null;

  /* State machine tracking internal variables
    - Two core states (decoupled by the debounce time):
      1) Whether we're loading
      2) Whether we should display the loading indicator
    - We need to use a setTimeout callback to clear the loading indicator, because
      after we transition to not loading onRedraw may not be called again within DEBOUNCE_MS
  */
  private isCurrentlyLoading: boolean; // Loading status
  private shouldShowLoadingIndicator: boolean; // Display status
  private stateChangeTimestamp: number; // When we last changed state
  private removeLoadingIndicatorTimeoutId: number | null;

  constructor(props: LoadWidgetProps) {
    super(props);
    this.isCurrentlyLoading = false;
    this.stateChangeTimestamp = Date.now();
    this.shouldShowLoadingIndicator = false;
    this.removeLoadingIndicatorTimeoutId = null;
  }

  onRenderHTML(rootElement: HTMLElement): void {
    rootElement.innerHTML = `
      <div class="${classes.loadingIndicator}">
        <div class="${classes.wheel}"></div>
        <span>Loading map data</span>
      </div>
    `;
  }

  onAdd(): HTMLDivElement {
    const el = this.onCreateRootElement();
    el.id = 'map-data-loading-indicator';
    el.className = `${classes.loadingIndicatorContainer} ${classes.hidden}`;
    this.onRenderHTML(el);
    this.element = el;
    return el;
  }

  private toggleLoadingIndicator() {
    // Update Widget internal state
    this.shouldShowLoadingIndicator = !this.shouldShowLoadingIndicator;
    if (!this.element) {
      return;
    }
    if (this.shouldShowLoadingIndicator) {
      this.element.className = `${classes.loadingIndicatorContainer} ${classes.visible}`;
    } else {
      this.element.className = `${classes.loadingIndicatorContainer} ${classes.hidden}`;
    }
  }

  onRedraw(params: { layers: Layer[] }) {
    const { layers } = params;
    const currentlyLoading = layers.some((l) => !l.isLoaded);
    const now = Date.now();
    if (this.props.devModeEnabled) {
      const loadingLayers = layers.filter((l) => !l.isLoaded).map((l) => l.id);
      if (loadingLayers.length > 0) {
        // eslint-disable-next-line no-console
        console.log('Loading Layers: ', loadingLayers);
      }
    }

    // State transition between loading and not loading
    if (currentlyLoading !== this.isCurrentlyLoading) {
      this.stateChangeTimestamp = now;
      this.isCurrentlyLoading = currentlyLoading;
    }

    const timeSinceStateChange = now - this.stateChangeTimestamp;
    if (currentlyLoading) {
      if (
        timeSinceStateChange >= (this.props.delay ?? DEBOUNCE_MS) &&
        !this.shouldShowLoadingIndicator
      ) {
        this.toggleLoadingIndicator();
      }
    } else {
      // We're not currently loading
      if (this.shouldShowLoadingIndicator) {
        if (this.removeLoadingIndicatorTimeoutId !== null) {
          clearTimeout(this.removeLoadingIndicatorTimeoutId);
        }
        this.removeLoadingIndicatorTimeoutId = window.setTimeout(() => {
          this.toggleLoadingIndicator();
        }, DEBOUNCE_MS);
      }
    }
  }
}
